import { useState } from 'react';
import { ConsolePage, Icon, Segmented } from '@/components';
import { useCourses } from '@/hooks/queries';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useLocale } from '@/i18n/LocaleProvider';
import { formatDate } from '@/lib/format';
import type { Locale } from '@/types/domain';
import { CertFormal, CertMinimal, type CertPayload } from '../certificate/Certificate';

/**
 * Sample certificate for instructors to show stakeholders — renders the real
 * cert template (Formal/Minimal, EN/TH) with the live course + instructor names
 * but a placeholder participant. Not a real, verifiable certificate.
 */
export function InstructorCertPreview() {
  const { data: courses = [] } = useCourses();
  const course = courses[0];
  const isDesktop = useIsDesktop();
  const { locale } = useLocale();
  const [treatment, setTreatment] = useState<'formal' | 'minimal'>('formal');
  const [certLang, setCertLang] = useState<Locale>(locale);

  const seminarIso = course?.seminarDate ?? '2026-06-26';
  const expires = new Date(seminarIso);
  expires.setFullYear(expires.getFullYear() + (course?.certYears ?? 2));
  const signatures = [
    { name: 'พญ.เฌอมาลย์ จันทนโรจน์ MD, TCEP', role: 'Course Instructor' },
    { name: 'พญ.ดิฏฐาพร ธรรมมะ MD, TCEP', role: 'Course Instructor' },
  ];

  const sample: CertPayload = {
    id: 'WP-ACLS-2026-0001',
    course: course?.title.en ?? 'Advanced Cardiac Life Support — AHA 2025',
    th: course?.title.th ?? 'การช่วยฟื้นคืนชีพขั้นสูง',
    host: course?.host ?? 'Warm Paper ED',
    signatures,
    name: certLang === 'th' ? 'นาย ตัวอย่าง ใจดี' : 'Sample Participant',
    locale: certLang,
    issued: formatDate(seminarIso, certLang, { day: '2-digit', month: 'short', year: 'numeric' }),
    expires: formatDate(expires.toISOString().slice(0, 10), certLang, { day: '2-digit', month: 'short', year: 'numeric' }),
  };

  const Cert = treatment === 'formal' ? CertFormal : CertMinimal;
  const certW = isDesktop ? 680 : 338;

  return (
    <ConsolePage title="Certificate preview" th="ตัวอย่างใบรับรอง" crumbs="TEACHING" back="/teach" maxWidth={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 8, padding: '11px 14px', background: 'var(--ochre-soft)', borderRadius: 10, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          <Icon name="award" size={16} style={{ flex: 'none', marginTop: 1, color: 'var(--ochre)' }} />
          <span><strong>Sample only</strong> — this is the certificate a learner receives on completion. Real ones carry a unique ID and a verifiable QR code.</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Segmented value={treatment} onChange={setTreatment} options={[{ value: 'formal' as const, label: 'Formal' }, { value: 'minimal' as const, label: 'Minimal' }]} />
          <Segmented value={certLang} onChange={setCertLang} options={[{ value: 'en' as const, label: 'EN' }, { value: 'th' as const, label: 'ไทย' }]} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 20px' }}>
          <Cert d={sample} w={certW} />
        </div>
      </div>
    </ConsolePage>
  );
}
